import {
  WalletLedgerCategory,
  WalletLedgerDirection,
  WalletLedgerEntry,
  WalletLedgerResponse,
} from "../types/wallet";

const pickField = (
  raw: Record<string, unknown> | null | undefined,
  ...keys: string[]
) => {
  for (const key of keys) {
    const val = raw?.[key];
    if (val !== undefined && val !== null) return val;
  }
  return undefined;
};

const isSuccessStatus = (status: string) => {
  const normalized = status.trim().toUpperCase();
  return normalized === "SUCCESS" || normalized.startsWith("SUCCESS");
};

const parseDate = (raw: Record<string, unknown>): string => {
  const value =
    pickField(raw, "date", "Date", "paymentDate", "PaymentDate", "createdAt", "CreatedAt") ??
    "";
  return String(value);
};

export const categoryLabelKey = (category: WalletLedgerCategory): string => {
  switch (category) {
    case "TOP_UP_ZAIN":
      return "walletPage.categoryTopUpZain";
    case "TOP_UP_CARD":
      return "walletPage.categoryTopUpCard";
    case "TOP_UP_QICARD":
      return "walletPage.categoryTopUpQiCard";
    case "RETURN_CREDIT":
      return "walletPage.categoryReturnCredit";
    case "ORDER_PAYMENT":
      return "walletPage.categoryOrderPayment";
    default:
      return "walletPage.categoryOther";
  }
};

const classifyPaymentRow = (
  raw: Record<string, unknown>
): { direction: WalletLedgerDirection; category: WalletLedgerCategory } | null => {
  const invoiceId = String(pickField(raw, "invoiceId", "InvoiceId") ?? "").toLowerCase();
  const type = String(pickField(raw, "type", "Type") ?? "");
  const orderId = String(pickField(raw, "orderId", "OrderId") ?? "");

  if (invoiceId === "wallet") {
    if (type.toLowerCase() === "zaincash") {
      return { direction: "IN", category: "TOP_UP_ZAIN" };
    }
    if (type.toLowerCase() === "cardpayment") {
      return { direction: "IN", category: "TOP_UP_CARD" };
    }
    if (type.toLowerCase() === "qicard") {
      return { direction: "IN", category: "TOP_UP_QICARD" };
    }
    return { direction: "IN", category: "OTHER" };
  }

  if (type.toLowerCase() === "wallet" && orderId) {
    return { direction: "OUT", category: "ORDER_PAYMENT" };
  }

  return null;
};

/** Build ledger rows from GET /api/payments until backend ships unified transactions API. */
export const ledgerEntriesFromPayments = (
  rows: unknown[]
): WalletLedgerEntry[] => {
  if (!Array.isArray(rows)) return [];

  const entries: WalletLedgerEntry[] = [];

  for (const row of rows) {
    const raw = row as Record<string, unknown>;
    const status = String(pickField(raw, "status", "Status") ?? "");
    if (!isSuccessStatus(status)) continue;

    const classified = classifyPaymentRow(raw);
    if (!classified) continue;

    const amount = Math.abs(Number(pickField(raw, "amount", "Amount") ?? 0));
    const id = String(pickField(raw, "id", "Id", "transactionId", "TransactionId") ?? "");
    const orderId = String(pickField(raw, "orderId", "OrderId") ?? "") || undefined;
    const currency = String(pickField(raw, "currency", "Currency") ?? "IQD");
    const referenceId = String(
      pickField(raw, "transactionId", "TransactionId", "externalReferenceId", "ExternalReferenceId") ?? ""
    );

    entries.push({
      id: id || `${classified.category}-${parseDate(raw)}-${amount}`,
      direction: classified.direction,
      category: classified.category,
      amount,
      currency,
      date: parseDate(raw),
      description: "",
      referenceId: referenceId || undefined,
      orderId,
      status,
      isLegacySource: true,
    });
  }

  return entries.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
};

export const normalizeLedgerApiResponse = (data: unknown): WalletLedgerEntry[] => {
  if (!data) return [];

  const payload = data as Record<string, unknown> | WalletLedgerEntry[];
  const items = Array.isArray(payload)
    ? payload
    : (pickField(payload as Record<string, unknown>, "items", "Items") as unknown[]);

  if (!Array.isArray(items)) return [];

  return items
    .map((row) => {
      const raw = row as Record<string, unknown>;
      const directionRaw = String(
        pickField(raw, "direction", "Direction") ?? ""
      ).toUpperCase();
      const direction: WalletLedgerDirection =
        directionRaw === "OUT" ? "OUT" : "IN";

      const categoryRaw = String(
        pickField(raw, "category", "Category") ?? "OTHER"
      ).toUpperCase();

      const category: WalletLedgerCategory =
        categoryRaw === "TOP_UP_ZAIN"
          ? "TOP_UP_ZAIN"
          : categoryRaw === "TOP_UP_CARD"
            ? "TOP_UP_CARD"
            : categoryRaw === "TOP_UP_QICARD"
              ? "TOP_UP_QICARD"
              : categoryRaw === "RETURN_CREDIT"
              ? "RETURN_CREDIT"
              : categoryRaw === "ORDER_PAYMENT"
                ? "ORDER_PAYMENT"
                : "OTHER";

      return {
        id: String(pickField(raw, "id", "Id") ?? ""),
        direction,
        category,
        amount: Math.abs(Number(pickField(raw, "amount", "Amount") ?? 0)),
        currency: String(pickField(raw, "currency", "Currency") ?? "IQD"),
        date: parseDate(raw),
        description: String(pickField(raw, "description", "Description") ?? ""),
        referenceId: String(pickField(raw, "referenceId", "ReferenceId") ?? "") || undefined,
        orderId: String(pickField(raw, "orderId", "OrderId") ?? "") || undefined,
        status: String(pickField(raw, "status", "Status") ?? "SUCCESS"),
        isLegacySource: false,
      } satisfies WalletLedgerEntry;
    })
    .filter((entry) => entry.id && entry.amount !== 0)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const normalizeLedgerResponse = (data: unknown): WalletLedgerResponse => {
  if (!data || typeof data !== "object") {
    return { items: normalizeLedgerApiResponse(data) };
  }

  const raw = data as Record<string, unknown>;
  return {
    items: normalizeLedgerApiResponse(data),
    balance: Number(pickField(raw, "balance", "Balance") ?? NaN) || undefined,
    currency: String(pickField(raw, "currency", "Currency") ?? "") || undefined,
  };
};

export const filterLedgerByTab = (
  entries: WalletLedgerEntry[],
  tab: "moneyIn" | "moneyOut" | "all"
): WalletLedgerEntry[] => {
  if (tab === "all") return entries;
  if (tab === "moneyIn") return entries.filter((e) => e.direction === "IN");
  return entries.filter((e) => e.direction === "OUT");
};
